import Slider from '@react-native-community/slider';
import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  AlignJustify,
  Boxes,
  Building2,
  Camera,
  Car,
  CarFront,
  Check,
  ChevronLeft,
  Combine,
  Construction,
  Container,
  Droplet,
  Droplets,
  Flame,
  Grid3x3,
  HelpCircle,
  Layers,
  Minus,
  MoreHorizontal,
  Mountain,
  PaintBucket,
  Snowflake,
  Sofa,
  Square,
  Sun,
  TreeDeciduous,
  TreePine,
  Umbrella,
  Users,
  Warehouse,
  Wind,
  X,
  Zap,
} from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { useNotice } from '@/components/ConfirmDialog';
import { IconTilePicker, type IconTileOption } from '@/components/IconTilePicker';
import { NumberPicker } from '@/components/NumberPicker';
import { StickyBottomBar } from '@/components/StickyBottomBar';
import { YearPicker } from '@/components/YearPicker';
import attributeRules from '@/data/attribute-rules';

/** How many task rules in the catalog key on a given lifestyle flag.
 *  Powers the "+N tasks" pill on each ToggleRow — same affordance the
 *  onboarding lifestyle step uses, just lifted into Edit Home so users
 *  see the cost of every flip without leaving Settings. */
const lifestyleTaskCount = (ruleField: string): number =>
  attributeRules.filter(
    (r) => (r.applies_to as Record<string, unknown>)[ruleField] === true
  ).length;
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import {
  REGION_HAZARD_LABEL,
  REGION_HAZARD_ORDER,
  parseRegionHazards,
} from '@/data/region-hazards';
import type {
  Basement,
  ExteriorMaterial,
  Foundation,
  Garage,
  HvacType,
  PropertyRow,
  RegionHazard,
  RoofMaterial,
  Sewer,
  WaterSource,
} from '@/db/types';
import { now } from '@/db/ids';
import { yearBandFromYearBuilt } from '@/db/persist-onboarding';
import { bumpDataRevision, useDataRevision } from '@/lib/data-revision';
import { ReplanSummaryModal } from '@/components/ReplanSummaryModal';
import {
  getActiveProperty,
  regenerateTasksForProperty,
  regenerateTasksWithDiff,
  type ReplanDiff,
} from '@/lib/tasks';
import { persistPickedFile } from '@/lib/files';
import { goBackOrTo } from '@/lib/nav';
import { webNoAutofill, webNoOutline } from '@/lib/web-style';

const BASEMENT_OPTS: { v: Basement; label: string }[] = [
  { v: 'none', label: 'None / slab' },
  { v: 'crawl', label: 'Crawl space' },
  { v: 'unfinished', label: 'Unfinished basement' },
  { v: 'finished', label: 'Finished basement' },
  { v: 'partial', label: 'Partial basement' },
];
const GARAGE_OPTS: { v: Garage; label: string }[] = [
  { v: 'none', label: 'No garage' },
  { v: '1-car', label: '1-car' },
  { v: '2-car', label: '2-car' },
  { v: '3-car', label: '3+ car' },
  { v: 'detached', label: 'Detached' },
  { v: 'carport', label: 'Carport' },
];
const WATER_OPTS: { v: WaterSource; label: string }[] = [
  { v: 'city', label: 'City water' },
  { v: 'well', label: 'Well' },
  { v: 'mixed', label: 'Mixed' },
  { v: 'unsure', label: 'Not sure' },
];
const SEWER_OPTS: { v: Sewer; label: string }[] = [
  { v: 'city', label: 'City sewer' },
  { v: 'septic', label: 'Septic' },
  { v: 'shared', label: 'Shared / community' },
  { v: 'unsure', label: 'Not sure' },
];
const ROOF_OPTS: { v: RoofMaterial; label: string }[] = [
  { v: 'asphalt', label: 'Asphalt shingles' },
  { v: 'metal', label: 'Metal' },
  { v: 'tile', label: 'Tile' },
  { v: 'slate', label: 'Slate' },
  { v: 'wood', label: 'Wood shake' },
  { v: 'flat', label: 'Flat / membrane' },
  { v: 'unsure', label: 'Not sure' },
];
const FOUNDATION_OPTS: { v: Foundation; label: string }[] = [
  { v: 'slab', label: 'Slab on grade' },
  { v: 'crawl', label: 'Crawl space' },
  { v: 'basement', label: 'Basement' },
  { v: 'pier', label: 'Pier / post' },
  { v: 'unsure', label: 'Not sure' },
];
const EXTERIOR_OPTS: { v: ExteriorMaterial; label: string }[] = [
  { v: 'vinyl', label: 'Vinyl siding' },
  { v: 'brick', label: 'Brick' },
  { v: 'wood', label: 'Wood siding' },
  { v: 'stucco', label: 'Stucco' },
  { v: 'fiber-cement', label: 'Fiber cement' },
  { v: 'stone', label: 'Stone' },
  { v: 'aluminum', label: 'Aluminum' },
  { v: 'log', label: 'Log' },
  { v: 'other', label: 'Other / mixed' },
];
const HVAC_OPTS: { v: HvacType; label: string }[] = [
  { v: 'furnace', label: 'Forced-air furnace + AC' },
  { v: 'boiler', label: 'Boiler + radiators' },
  { v: 'heatpump', label: 'Heat pump' },
  { v: 'mini', label: 'Mini-split' },
  { v: 'baseboard', label: 'Electric baseboard' },
  { v: 'radiant', label: 'Radiant floor' },
  { v: 'unsure', label: 'Not sure' },
];

type HeatingFuel = NonNullable<PropertyRow['heating_fuel']>;
const HEATING_FUEL_OPTS: { v: HeatingFuel; label: string }[] = [
  { v: 'gas', label: 'Natural gas' },
  { v: 'electric', label: 'Electric' },
  { v: 'oil', label: 'Oil' },
  { v: 'propane', label: 'Propane' },
  { v: 'dual-fuel', label: 'Dual-fuel' },
  { v: 'unsure', label: 'Not sure' },
];

// Icon-decorated variants for the new IconTilePicker. Same values as
// the legacy *_OPTS arrays above (kept for the modal PickField that
// may still appear in onboarding) — just enriched with a lucide icon
// so each option reads visually at a glance instead of via text alone.
const BASEMENT_TILES: IconTileOption<Basement>[] = [
  { v: 'none', label: 'None / slab', Icon: Square },
  { v: 'crawl', label: 'Crawl space', Icon: Boxes },
  { v: 'unfinished', label: 'Unfinished basement', Icon: Construction },
  { v: 'finished', label: 'Finished basement', Icon: Sofa },
  { v: 'partial', label: 'Partial basement', Icon: Layers },
];
const FOUNDATION_TILES: IconTileOption<Foundation>[] = [
  { v: 'slab', label: 'Slab on grade', Icon: Square },
  { v: 'crawl', label: 'Crawl space', Icon: Boxes },
  { v: 'basement', label: 'Basement', Icon: Layers },
  { v: 'pier', label: 'Pier / post', Icon: AlignJustify },
  { v: 'unsure', label: 'Not sure', Icon: HelpCircle },
];
const ROOF_TILES: IconTileOption<RoofMaterial>[] = [
  { v: 'asphalt', label: 'Asphalt shingles', Icon: Square },
  { v: 'metal', label: 'Metal', Icon: Layers },
  { v: 'tile', label: 'Tile', Icon: Grid3x3 },
  { v: 'slate', label: 'Slate', Icon: Mountain },
  { v: 'wood', label: 'Wood shake', Icon: TreePine },
  { v: 'flat', label: 'Flat / membrane', Icon: Minus },
  { v: 'unsure', label: 'Not sure', Icon: HelpCircle },
];
const EXTERIOR_TILES: IconTileOption<ExteriorMaterial>[] = [
  { v: 'vinyl', label: 'Vinyl siding', Icon: Square },
  { v: 'brick', label: 'Brick', Icon: Boxes },
  { v: 'wood', label: 'Wood siding', Icon: TreePine },
  { v: 'stucco', label: 'Stucco', Icon: PaintBucket },
  { v: 'fiber-cement', label: 'Fiber cement', Icon: Layers },
  { v: 'stone', label: 'Stone', Icon: Mountain },
  { v: 'aluminum', label: 'Aluminum', Icon: AlignJustify },
  { v: 'log', label: 'Log', Icon: TreeDeciduous },
  { v: 'other', label: 'Other / mixed', Icon: MoreHorizontal },
];
const GARAGE_TILES: IconTileOption<Garage>[] = [
  { v: 'none', label: 'No garage', Icon: Square },
  { v: '1-car', label: '1-car', Icon: Car },
  { v: '2-car', label: '2-car', Icon: Car },
  { v: '3-car', label: '3+ car', Icon: CarFront },
  { v: 'detached', label: 'Detached', Icon: Warehouse },
  { v: 'carport', label: 'Carport', Icon: Umbrella },
];
const WATER_TILES: IconTileOption<WaterSource>[] = [
  { v: 'city', label: 'City water', Icon: Building2 },
  { v: 'well', label: 'Well', Icon: Droplets },
  { v: 'mixed', label: 'Mixed', Icon: Combine },
  { v: 'unsure', label: 'Not sure', Icon: HelpCircle },
];
const SEWER_TILES: IconTileOption<Sewer>[] = [
  { v: 'city', label: 'City sewer', Icon: Building2 },
  { v: 'septic', label: 'Septic', Icon: Container },
  { v: 'shared', label: 'Shared / community', Icon: Users },
  { v: 'unsure', label: 'Not sure', Icon: HelpCircle },
];
const HEATING_FUEL_TILES: IconTileOption<HeatingFuel>[] = [
  { v: 'gas', label: 'Natural gas', Icon: Flame },
  { v: 'electric', label: 'Electric', Icon: Zap },
  { v: 'oil', label: 'Oil', Icon: Droplet },
  { v: 'propane', label: 'Propane', Icon: Container },
  { v: 'dual-fuel', label: 'Dual-fuel', Icon: Combine },
  { v: 'unsure', label: 'Not sure', Icon: HelpCircle },
];
const SECONDARY_HVAC_TILES: IconTileOption<HvacType | 'none'>[] = [
  { v: 'none', label: 'None', Icon: Square },
  { v: 'furnace', label: 'Furnace + AC', Icon: Flame },
  { v: 'boiler', label: 'Boiler', Icon: Container },
  { v: 'heatpump', label: 'Heat pump', Icon: Snowflake },
  { v: 'mini', label: 'Mini-split', Icon: Wind },
  { v: 'baseboard', label: 'Baseboard', Icon: AlignJustify },
  { v: 'radiant', label: 'Radiant floor', Icon: Sun },
];

export default function EditProperty() {
  const router = useRouter();
  const db = useSQLiteContext();
  const notice = useNotice();
  const [row, setRow] = useState<PropertyRow | null>(null);
  const [name, setName] = useState('');
  const [stories, setStories] = useState('');
  // Build year is stored as a 4-digit string in the input and parsed on
  // save. Schema v33 column is INTEGER (e.g. 1998). Drives age-based
  // task scheduling + system year inheritance.
  const [yearBuilt, setYearBuilt] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [sqft, setSqft] = useState('');
  const [basement, setBasement] = useState<Basement | null>(null);
  const [garage, setGarage] = useState<Garage | null>(null);
  const [waterSource, setWaterSource] = useState<WaterSource | null>(null);
  const [sewer, setSewer] = useState<Sewer | null>(null);
  const [roof, setRoof] = useState<RoofMaterial | null>(null);
  const [foundation, setFoundation] = useState<Foundation | null>(null);
  const [exterior, setExterior] = useState<ExteriorMaterial | null>(null);
  // Multi-value exterior — same pattern as roofMaterials / foundations
  // already in v32 schema. Most houses are mixed (vinyl + brick front,
  // wood + stucco, etc.) so a single pick under-represents the truth
  // and trims paint/sealing tasks accordingly. The legacy `exterior`
  // single-value is kept in sync (mirrors the first element) so
  // existing rules + Edit Home consumers don't need to change.
  const [exteriors, setExteriors] = useState<ExteriorMaterial[]>([]);
  const [secondaryHvac, setSecondaryHvac] = useState<HvacType | null>(null);
  const [heatingFuel, setHeatingFuel] = useState<HeatingFuel | null>(null);
  // Stored as a 4-digit string in the input and parsed on save, same
  // shape as yearBuilt. Schema column is INTEGER. Drives onboarding
  // tone (fresh-owner vs long-term tracker).
  const [ownedSince, setOwnedSince] = useState('');
  const [backgroundUri, setBackgroundUri] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  const [zip, setZip] = useState('');
  const [lat, setLat] = useState<number | null>(null);
  const [lon, setLon] = useState<number | null>(null);
  const [hasPool, setHasPool] = useState(false);
  const [hasSolar, setHasSolar] = useState(false);
  const [hasPets, setHasPets] = useState(false);
  const [hasKids, setHasKids] = useState(false);
  const [hasHoa, setHasHoa] = useState(false);
  const [hasWoodfire, setHasWoodfire] = useState(false);
  const [hasSmartHome, setHasSmartHome] = useState(false);
  const [hasStrHost, setHasStrHost] = useState(false);
  const [hasHeavySnow, setHasHeavySnow] = useState(false);
  const [hasGarden, setHasGarden] = useState(false);
  const [hasAccessibility, setHasAccessibility] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);
  // v35 lifestyles — newer segment toggles. Same shape as the originals
  // above, kept in their own group to make the diff obvious.
  const [travelsOften, setTravelsOften] = useState(false);
  const [worksFromHome, setWorksFromHome] = useState(false);
  const [hasAllergies, setHasAllergies] = useState(false);
  const [hasNewborn, setHasNewborn] = useState(false);
  const [multiGenHousehold, setMultiGenHousehold] = useState(false);
  const [over60Owner, setOver60Owner] = useState(false);
  const [hasLivestock, setHasLivestock] = useState(false);
  const [hasShortTermContractors, setHasShortTermContractors] = useState(false);
  const [regionHazards, setRegionHazards] = useState<RegionHazard[]>([]);
  const [working, setWorking] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  // Detailed scrollable replan summary, opened after the user hits the
  // sticky-bar Replan button. Replaces the single-line notice() toast.
  const [replanSummary, setReplanSummary] = useState<ReplanDiff | null>(null);
  const [replanLoading, setReplanLoading] = useState(false);
  // Spinner overlay while we persist the just-picked photo from cache
  // to the app's document directory.
  const [photoBusy, setPhotoBusy] = useState(false);
  // Debounce timer for the needs_replan flag write. Auto-replan was
  // removed in Wave 22; this only flips a 1/0 bit on the property row
  // so the UI knows when the schedule is out of sync with the profile.
  const replanTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks whether the initial load has completed; we don't want the
  // auto-save effect to fire its first run on the values populated by
  // the load (which would be a redundant write of the same data).
  const loadedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks which property's data is currently loaded. Used to suppress
  // self-induced revision bumps (auto-save → bumpDataRevision → load
  // effect → re-fetch → setRow with a new object reference → auto-save
  // effect re-runs → infinite save loop). When a revision bump comes
  // in for the SAME property id we're already showing, skip the
  // re-fetch entirely. Real home-switch bumps land a different id and
  // do trigger a reload.
  const lastLoadedIdRef = useRef<string | null>(null);
  // Subscribe to the data-revision bus so switching the active home
  // from the right sidebar / top-bar switcher re-loads this page's
  // form with the newly-active property. Without this, the user
  // switched home but the form still showed the previous home's
  // attributes — leading to "I edited the wrong house" surprises.
  const revision = useDataRevision();

  useEffect(() => {
    (async () => {
      // Cheap pre-check: read just the id first so a self-bump can
      // bail without touching any setters. This was the cause of the
      // "Edit Home is saving over and over" bug — auto-save bumps
      // revision, revision triggers this effect, this effect setRow's
      // a fresh object reference, that re-triggers the auto-save
      // useEffect (deps include `row`), and on we go.
      const p = await getActiveProperty(db);
      if (!p) return;
      if (lastLoadedIdRef.current === p.id) return;
      lastLoadedIdRef.current = p.id;
      // Reset the loaded flag so the auto-save effect doesn't fire on
      // the freshly-loaded values from the new property.
      loadedRef.current = false;
      setRow(p);
      setName(p.name ?? '');
      // Stories chips show 1 / 2 / 3+. Anything 3+ collapses to the
      // "3+" chip on load so the highlight matches.
      setStories(
        p.stories ? (p.stories >= 3 ? '3+' : String(p.stories)) : ''
      );
      setYearBuilt(p.year_built ? String(p.year_built) : '');
      setBedrooms(p.bedrooms ? String(p.bedrooms) : '');
      setBathrooms(p.bathrooms ? String(p.bathrooms) : '');
      setSqft(p.sqft ? String(p.sqft) : '');
      setBasement(p.basement);
      setGarage(p.garage);
      setWaterSource(p.water_source);
      setSewer(p.sewer);
      setRoof(p.roof_material);
      setFoundation(p.foundation);
      setExterior(p.exterior_material);
      // Hydrate the multi-select array — falls back to a single-element
      // array built from the legacy single-value column so users who
      // were on the old PickField still see their pick highlighted.
      const exJson = p.exterior_materials_json
        ? (JSON.parse(p.exterior_materials_json) as ExteriorMaterial[])
        : null;
      setExteriors(
        exJson && exJson.length > 0
          ? exJson
          : p.exterior_material
          ? [p.exterior_material]
          : []
      );
      setSecondaryHvac(p.secondary_hvac_type);
      setHeatingFuel(p.heating_fuel);
      setOwnedSince(p.owned_since ? String(p.owned_since) : '');
      setBackgroundUri(p.background_uri);
      setAddress(p.address ?? '');
      setZip(p.zip ?? '');
      setLat(p.lat ?? null);
      setLon(p.lon ?? null);
      setHasPool(p.has_pool === 1);
      setHasSolar(p.has_solar === 1);
      setHasPets(p.has_pets === 1);
      setHasKids(p.has_kids === 1);
      setHasHoa(p.has_hoa === 1);
      setHasWoodfire(p.has_woodfire === 1);
      setHasSmartHome(p.has_smart_home === 1);
      setHasStrHost(p.has_str_host === 1);
      setHasHeavySnow(p.has_heavy_snow === 1);
      setHasGarden(p.has_garden === 1);
      setHasAccessibility(p.has_accessibility === 1);
      setIsFirstTime(p.is_first_time === 1);
      // v35 lifestyle hydration. Coerce to a string of '0'/'1' via === 1
      // so legacy null values surface as `false`.
      setTravelsOften(p.travels_often === 1);
      setWorksFromHome(p.works_from_home === 1);
      setHasAllergies(p.has_allergies === 1);
      setHasNewborn(p.has_newborn === 1);
      setMultiGenHousehold(p.multi_gen_household === 1);
      setOver60Owner(p.over_60_owner === 1);
      setHasLivestock(p.has_livestock === 1);
      setHasShortTermContractors(p.has_short_term_contractors === 1);
      setRegionHazards(parseRegionHazards(p.region_hazards_json));
      // Defer enabling auto-save by a microtask so the state setters
      // above commit before the first effect run picks them up — keeps
      // us from issuing a duplicate write of the just-loaded values.
      setTimeout(() => { loadedRef.current = true; }, 0);
    })();
  }, [db, revision]);

  // Auto-save: every state change writes the full row after a 500ms
  // pause. No "Save" button needed — the user can navigate away the
  // moment they're done editing. The heavy regenerateTasksForProperty
  // call is intentionally NOT triggered here (it's expensive to run
  // on every keystroke); the user can hit "Replan year" in Settings if
  // they want the planner to absorb structural changes.
  // The one true field-persist. Debounced by the auto-save effect
  // below; called directly (flush) by the explicit Save button in the
  // sticky footer (Wave 47 — the button exists purely for confidence:
  // some users don't trust "auto-saves as you go" until they've pressed
  // something).
  const persistNow = useCallback(async () => {
    if (!loadedRef.current || !row) return;
      try {
        const parsedYear = yearBuilt ? parseInt(yearBuilt, 10) : null;
        const thisYear = new Date().getFullYear();
        // Clamp parsed year to a sane range so a half-typed value
        // ("19") doesn't slip in as 19 AD.
        const goodYear =
          parsedYear !== null && Number.isFinite(parsedYear) &&
          parsedYear >= 1700 && parsedYear <= thisYear + 1
            ? parsedYear
            : null;
        // Derive year_band from the precise year so rule paths that still
        // key on the band keep working without a separate edit.
        const derivedBand = yearBandFromYearBuilt(goodYear);
        const parsedOwned = ownedSince ? parseInt(ownedSince, 10) : null;
        // Same clamp shape as build year. Don't let an ownership year
        // older than the build year through — it implies a typo more
        // than a real value.
        const goodOwned =
          parsedOwned !== null && Number.isFinite(parsedOwned) &&
          parsedOwned >= 1700 && parsedOwned <= thisYear &&
          (goodYear === null || parsedOwned >= goodYear)
            ? parsedOwned
            : null;
        await db.runAsync(
          `UPDATE property SET
            name = ?, stories = ?, year_built = ?, year_band = COALESCE(?, year_band),
            owned_since = ?, heating_fuel = ?, region_hazards_json = ?,
            bedrooms = ?, bathrooms = ?, sqft = ?,
            basement = ?, garage = ?, water_source = ?, sewer = ?,
            roof_material = ?, foundation = ?, exterior_material = ?,
            exterior_materials_json = ?,
            secondary_hvac_type = ?, background_uri = ?,
            address = ?, zip = ?, lat = ?, lon = ?,
            has_pool = ?, has_solar = ?, has_pets = ?, has_kids = ?,
            has_hoa = ?, has_woodfire = ?, has_smart_home = ?, has_str_host = ?,
            has_heavy_snow = ?, has_garden = ?, has_accessibility = ?, is_first_time = ?,
            travels_often = ?, works_from_home = ?, has_allergies = ?, has_newborn = ?,
            multi_gen_household = ?, over_60_owner = ?, has_livestock = ?, has_short_term_contractors = ?,
            updated_at = ?
           WHERE id = ?`,
          name.trim() || null,
          stories ? parseInt(stories, 10) || null : null,
          goodYear,
          derivedBand,
          goodOwned,
          heatingFuel,
          regionHazards.length > 0 ? JSON.stringify(regionHazards) : null,
          bedrooms ? parseInt(bedrooms, 10) || null : null,
          bathrooms ? parseFloat(bathrooms) || null : null,
          sqft ? parseInt(sqft, 10) || null : null,
          basement, garage, waterSource, sewer,
          roof, foundation, exteriors[0] ?? exterior,
          exteriors.length > 0 ? JSON.stringify(exteriors) : null,
          secondaryHvac,
          backgroundUri,
          address.trim() || null,
          zip.trim() || null,
          lat, lon,
          hasPool ? 1 : 0, hasSolar ? 1 : 0, hasPets ? 1 : 0, hasKids ? 1 : 0,
          hasHoa ? 1 : 0, hasWoodfire ? 1 : 0, hasSmartHome ? 1 : 0, hasStrHost ? 1 : 0,
          hasHeavySnow ? 1 : 0, hasGarden ? 1 : 0, hasAccessibility ? 1 : 0,
          isFirstTime ? 1 : 0,
          travelsOften ? 1 : 0, worksFromHome ? 1 : 0, hasAllergies ? 1 : 0, hasNewborn ? 1 : 0,
          multiGenHousehold ? 1 : 0, over60Owner ? 1 : 0, hasLivestock ? 1 : 0,
          hasShortTermContractors ? 1 : 0,
          now(),
          row.id
        );
        // Tell every subscriber (AppHeader avatar/ticker, RightRail /
        // DesktopSidebar nickname + address, Home greeting) to re-read
        // immediately. Without this the user sees the edit form update
        // but the desktop sidebar continues to show the old home
        // nickname until a manual nav.
        bumpDataRevision();
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 1400);
      } catch (err) {
        console.warn('property auto-save failed', err);
      }
  }, [
    row, name, stories, yearBuilt, ownedSince, heatingFuel,
    bedrooms, bathrooms, sqft,
    basement, garage, waterSource, sewer, roof, foundation, exterior, exteriors,
    secondaryHvac, backgroundUri, address, zip, lat, lon,
    hasPool, hasSolar, hasPets, hasKids, hasHoa, hasWoodfire,
    hasSmartHome, hasStrHost, hasHeavySnow, hasGarden,
    hasAccessibility, isFirstTime,
    travelsOften, worksFromHome, hasAllergies, hasNewborn,
    multiGenHousehold, over60Owner, hasLivestock, hasShortTermContractors,
    regionHazards, db,
  ]);

  // Debounced auto-save: any field change re-arms a 500ms timer that
  // fires persistNow.
  useEffect(() => {
    if (!loadedRef.current || !row) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void persistNow();
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [persistNow, row]);

  // Mark the property as "needs replan" when any planner-affecting
  // field changes. We do NOT auto-replan anymore — that was rebuilding
  // the entire 63-task schedule on every keystroke, which:
  //   - showed misleading "63 tasks rescheduled" notices every time
  //     (sub-day clock jitter masquerading as date changes), and
  //   - would have thrashed Google Calendar sync (60+ event recreations
  //     for a single basement-type toggle).
  // The flag here lights up the Replan icon badge on My Home and the
  // sticky bar so the user always knows when their schedule is out of
  // sync with their home profile — and the regen only fires when they
  // explicitly hit Replan.
  useEffect(() => {
    if (!loadedRef.current || !row) return;
    if (replanTimerRef.current) clearTimeout(replanTimerRef.current);
    replanTimerRef.current = setTimeout(async () => {
      try {
        await db.runAsync(
          `UPDATE property SET needs_replan = 1, updated_at = ? WHERE id = ?`,
          now(),
          row.id
        );
        bumpDataRevision();
      } catch (err) {
        console.warn('flag needs_replan failed', err);
      }
    }, 600);
    return () => {
      if (replanTimerRef.current) clearTimeout(replanTimerRef.current);
    };
  }, [
    row,
    // Lifestyle flags drive most of the attribute rules.
    hasPool, hasSolar, hasPets, hasKids, hasHoa, hasWoodfire,
    hasSmartHome, hasStrHost, hasHeavySnow, hasGarden,
    hasAccessibility, isFirstTime,
    // v35 lifestyle flags.
    travelsOften, worksFromHome, hasAllergies, hasNewborn,
    multiGenHousehold, over60Owner, hasLivestock, hasShortTermContractors,
    // Region hazards gate the storm-prep bundle.
    regionHazards,
    // Climate / system membership that gate rules.
    secondaryHvac, zip, heatingFuel,
    // Structural attributes that change which rules fire.
    basement, foundation, roof, exteriors, waterSource, sewer,
    yearBuilt,
    db,
  ]);

  if (!row) return <View style={styles.container} />;

  const pickBackground = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.8,
      aspect: [16, 9],
      allowsEditing: true,
    });
    if (res.canceled || !res.assets[0]) return;
    const a = res.assets[0];
    // Copying the picked file into our document directory can take a
    // noticeable second on large images. Show a spinner overlay so the
    // user sees the system thinking instead of an unresponsive tile.
    setPhotoBusy(true);
    try {
      const persisted = await persistPickedFile(
        a.uri,
        a.fileName ?? `home-${Date.now()}.jpg`
      );
      setBackgroundUri(persisted);
    } finally {
      setPhotoBusy(false);
    }
  };

  const save = async () => {
    setWorking(true);
    try {
      // Wrap the write so a SQLite worker error surfaces clearly instead
      // of bubbling up as an "Unknown" Expo error overlay.
      const parsedYear = yearBuilt ? parseInt(yearBuilt, 10) : null;
      const thisYear = new Date().getFullYear();
      const goodYear =
        parsedYear !== null && Number.isFinite(parsedYear) &&
        parsedYear >= 1700 && parsedYear <= thisYear + 1
          ? parsedYear
          : null;
      const derivedBand = yearBandFromYearBuilt(goodYear);
      const parsedOwned = ownedSince ? parseInt(ownedSince, 10) : null;
      const goodOwned =
        parsedOwned !== null && Number.isFinite(parsedOwned) &&
        parsedOwned >= 1700 && parsedOwned <= thisYear &&
        (goodYear === null || parsedOwned >= goodYear)
          ? parsedOwned
          : null;
      await db.runAsync(
        `UPDATE property SET
          name = ?, stories = ?, year_built = ?, year_band = COALESCE(?, year_band),
          owned_since = ?, heating_fuel = ?, region_hazards_json = ?,
          bedrooms = ?, bathrooms = ?, sqft = ?,
          basement = ?, garage = ?, water_source = ?, sewer = ?,
          roof_material = ?, foundation = ?, exterior_material = ?,
          exterior_materials_json = ?,
          secondary_hvac_type = ?, background_uri = ?,
          address = ?, zip = ?, lat = ?, lon = ?,
          has_pool = ?, has_solar = ?, has_pets = ?, has_kids = ?,
          has_hoa = ?, has_woodfire = ?, has_smart_home = ?, has_str_host = ?,
          has_heavy_snow = ?, has_garden = ?, has_accessibility = ?, is_first_time = ?,
          travels_often = ?, works_from_home = ?, has_allergies = ?, has_newborn = ?,
          multi_gen_household = ?, over_60_owner = ?, has_livestock = ?, has_short_term_contractors = ?,
          updated_at = ?
         WHERE id = ?`,
        name.trim() || null,
        stories ? parseInt(stories, 10) || null : null,
        goodYear,
        derivedBand,
        goodOwned,
        heatingFuel,
        regionHazards.length > 0 ? JSON.stringify(regionHazards) : null,
        bedrooms ? parseInt(bedrooms, 10) || null : null,
        bathrooms ? parseFloat(bathrooms) || null : null,
        sqft ? parseInt(sqft, 10) || null : null,
        basement,
        garage,
        waterSource,
        sewer,
        roof,
        foundation,
        exteriors[0] ?? exterior,
        exteriors.length > 0 ? JSON.stringify(exteriors) : null,
        secondaryHvac,
        backgroundUri,
        address.trim() || null,
        zip.trim() || null,
        lat,
        lon,
        hasPool ? 1 : 0,
        hasSolar ? 1 : 0,
        hasPets ? 1 : 0,
        hasKids ? 1 : 0,
        hasHoa ? 1 : 0,
        hasWoodfire ? 1 : 0,
        hasSmartHome ? 1 : 0,
        hasStrHost ? 1 : 0,
        hasHeavySnow ? 1 : 0,
        hasGarden ? 1 : 0,
        hasAccessibility ? 1 : 0,
        isFirstTime ? 1 : 0,
        travelsOften ? 1 : 0,
        worksFromHome ? 1 : 0,
        hasAllergies ? 1 : 0,
        hasNewborn ? 1 : 0,
        multiGenHousehold ? 1 : 0,
        over60Owner ? 1 : 0,
        hasLivestock ? 1 : 0,
        hasShortTermContractors ? 1 : 0,
        now(),
        row.id
      );
      // Open the preview modal so the user sees what WOULD change
      // before any regen runs. The modal's "Replan now" button is the
      // actual commit step (handled by onConfirmReplan below).
      const { previewReplanDiff } = await import('@/lib/tasks');
      setReplanLoading(true);
      setReplanSummary({ added: 0, removed: 0, rescheduled: 0, addedTitles: [], removedTitles: [], rescheduledTitles: [] });
      const diff = await previewReplanDiff(db, row.id);
      setReplanSummary(diff);
      setReplanLoading(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('property save failed', err);
      void notice({ title: "Couldn't save", message: msg, tone: 'error' });
    } finally {
      setWorking(false);
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/(tabs)/my-home')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Edit my home</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView
        contentContainerStyle={{ paddingBottom: 140 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled">
        <Text style={styles.h1}>Tell us more about your home.</Text>
        <Text style={styles.sub}>
          Every detail tunes the plan and feeds the resale binder. All optional.
        </Text>

        <Section title="PERSONAL TOUCH">
          <Text style={styles.fieldLabel}>HOME PHOTO</Text>
          <Pressable
            onPress={pickBackground}
            disabled={photoBusy}
            style={styles.bgPicker}>
            {backgroundUri ? (
              <>
                <Image source={{ uri: backgroundUri }} style={styles.bgImage} />
                <Pressable
                  onPress={() => setBackgroundUri(null)}
                  style={styles.bgClear}
                  hitSlop={8}>
                  <X size={14} color={Palette.cream} strokeWidth={2.4} />
                </Pressable>
              </>
            ) : (
              <>
                {/* Default placeholder photo so the tile reads as "your
                    home photo lives here" instead of a blank tap-zone.
                    The user's own photo overlays this once picked. */}
                <Image
                  source={require('@/assets/images/default-profile.png')}
                  style={[styles.bgImage, { opacity: 0.6 }]}
                />
                <View style={[styles.bgEmpty, styles.bgEmptyOverlay]}>
                  <Camera size={28} color={Palette.cream} strokeWidth={1.8} />
                  <Text style={[styles.bgEmptyLabel, { color: Palette.cream }]}>
                    Tap to add a photo of your home
                  </Text>
                  <Text style={[styles.bgEmptySub, { color: 'rgba(251,246,234,0.85)' }]}>
                    Appears as the backdrop on your Home tab.
                  </Text>
                </View>
              </>
            )}
            {/* Loading overlay during the cache → document-dir copy.
                Covers both states (with photo and without) since the user
                may be replacing an existing photo. */}
            {photoBusy ? (
              <View style={styles.bgBusy}>
                <ActivityIndicator color={Palette.cream} />
                <Text style={styles.bgBusyText}>Saving photo…</Text>
              </View>
            ) : null}
          </Pressable>
        </Section>

        <Section title="ADDRESS">
          {/* Address (Places autocomplete) takes the wide left column;
              ZIP is the narrow right column on desktop. On mobile they
              stack via flex-wrap because the column isn't wide enough. */}
          <View style={styles.addrRow}>
            <View style={styles.addrAddress}>
              <Text style={styles.fieldLabel}>STREET ADDRESS</Text>
              <View style={{ marginTop: 5 }}>
                <AddressAutocomplete
                  value={address}
                  onChangeText={setAddress}
                  onPick={(detail) => {
                    setAddress(detail.address);
                    if (detail.zip) setZip(detail.zip);
                    if (detail.lat != null) setLat(detail.lat);
                    if (detail.lon != null) setLon(detail.lon);
                  }}
                />
              </View>
            </View>
            <View style={styles.addrZip}>
              <Field
                label="ZIP"
                value={zip}
                onChangeText={(v) => setZip(v.replace(/[^0-9]/g, '').slice(0, 5))}
                placeholder="48104"
                inputMode="numeric"
              />
            </View>
          </View>
          {lat != null && lon != null ? (
            <Text style={styles.addressHint}>
              Map ready — open My Home to see it.
            </Text>
          ) : (
            <Text style={styles.addressHint}>
              Tip: pick a Places suggestion above to unlock the map preview.
            </Text>
          )}
        </Section>

        <Section title="BASICS">
          {/* Row 1: Nickname + Stories. flex-wrap so Stories drops to
              its own line on narrow mobile. storiesCol carries the
              same marginTop as Field so labels line up on desktop. */}
          <View style={styles.nicknameRow}>
            <View style={styles.nicknameField}>
              <Field
                label="Home nickname"
                value={name}
                onChangeText={(v) => setName(v.slice(0, 30))}
                placeholder="The Smith House"
              />
            </View>
            <View style={styles.storiesCol}>
              <ChipNumber
                label="Stories"
                value={stories}
                onChange={setStories}
                options={['1', '2', '3+']}
              />
            </View>
          </View>
          {/* Row 2: Build year + Owned since (2 columns). Sq Ft moved
              out to its own slider row below since it's now a slider. */}
          <Pair>
            <View style={{ flex: 1 }}>
              <Text style={styles.fieldLabel}>BUILD YEAR</Text>
              <YearPicker
                value={yearBuilt ? Number(yearBuilt) : null}
                onChange={(y) => setYearBuilt(y ? String(y) : '')}
                placeholder="Pick a year"
                minYear={1700}
                title="Build year"
              />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.fieldLabel}>OWNED SINCE</Text>
              <YearPicker
                value={ownedSince ? Number(ownedSince) : null}
                onChange={(y) => setOwnedSince(y ? String(y) : '')}
                placeholder="Pick a year"
                minYear={yearBuilt ? Number(yearBuilt) : 1900}
                title="Owned since"
              />
            </View>
          </Pair>
          {/* Row 3: Sq Ft slider — full width on every viewport. Mirrors
              the onboarding slider's range (500-6000, step 100) so a
              user who toggled it during signup sees the same affordance
              here. Numeric label updates live as the thumb drags. */}
          <View style={styles.sqftBlock}>
            <View style={styles.sqftHead}>
              <Text style={styles.fieldLabel}>SQ FT</Text>
              <Text style={styles.sqftValue}>
                {sqft
                  ? (Number(sqft) >= 6000
                      ? '6,000+'
                      : Number(sqft).toLocaleString())
                  : '—'}
              </Text>
            </View>
            <Slider
              minimumValue={500}
              maximumValue={6000}
              step={100}
              value={sqft ? Number(sqft) : 1500}
              onValueChange={(v) => setSqft(String(Math.round(v)))}
              minimumTrackTintColor={Palette.forest}
              maximumTrackTintColor={Palette.sandDeep}
              thumbTintColor={Palette.forest}
            />
            <View style={styles.sqftEnds}>
              <Text style={styles.sqftEndLabel}>500</Text>
              <Text style={styles.sqftEndLabel}>6,000+</Text>
            </View>
          </View>
          {/* Row 4: Bedrooms + Bathrooms together. Both chip rows, both
              fit on desktop side-by-side; mobile wraps to a stack. */}
          <View style={styles.numberRow}>
            <View style={styles.numberCol}>
              <ChipNumber
                label="Bedrooms"
                value={bedrooms}
                onChange={setBedrooms}
                options={['1', '2', '3', '4', '5', '6+']}
              />
            </View>
            <View style={styles.numberCol}>
              <ChipNumber
                label="Bathrooms"
                value={bathrooms}
                onChange={setBathrooms}
                options={['1', '1.5', '2', '2.5', '3', '3.5', '4+']}
              />
            </View>
          </View>
        </Section>

        <Section title="STRUCTURE">
          <IconTilePicker<Basement> label="Basement" value={basement} options={BASEMENT_TILES} onChange={setBasement} />
          <IconTilePicker<Foundation> label="Foundation" value={foundation} options={FOUNDATION_TILES} onChange={setFoundation} />
          <IconTilePicker<RoofMaterial> label="Roof material" value={roof} options={ROOF_TILES} onChange={setRoof} />
          {/* Exterior is multi-select — most homes are mixed (vinyl
              front + brick chimney, fiber-cement + stone accents, etc).
              Picks are stored in exterior_materials_json and mirrored
              into the legacy exterior_material column for older rules. */}
          <IconTilePicker<ExteriorMaterial>
            mode="multi"
            label="Exterior (pick all that apply)"
            value={exteriors}
            options={EXTERIOR_TILES}
            onChange={setExteriors}
          />
          <IconTilePicker<Garage> label="Garage" value={garage} options={GARAGE_TILES} onChange={setGarage} />
        </Section>

        <Section title="UTILITIES">
          <IconTilePicker<WaterSource> label="Water source" value={waterSource} options={WATER_TILES} onChange={setWaterSource} />
          <IconTilePicker<Sewer> label="Sewer" value={sewer} options={SEWER_TILES} onChange={setSewer} />
          <IconTilePicker<HeatingFuel>
            label="Heating fuel"
            value={heatingFuel}
            options={HEATING_FUEL_TILES}
            onChange={setHeatingFuel}
          />
          {/* Secondary HVAC uses an 'none' sentinel because the schema
              column is nullable but IconTilePicker needs a string-only
              key per option. We translate at the boundary. */}
          <IconTilePicker<HvacType | 'none'>
            label="Secondary HVAC"
            value={(secondaryHvac ?? 'none') as HvacType | 'none'}
            options={SECONDARY_HVAC_TILES}
            onChange={(v) => setSecondaryHvac(v === 'none' || v === null ? null : v)}
          />
        </Section>

        <Section title="LIFESTYLE">
          <Text style={[styles.fieldLabel, { marginBottom: 8 }]}>
            Each toggle adds a small set of timely reminders — pool chemistry, solar
            production checks, pet HVAC filter, child safety walks.
          </Text>
          <ToggleRow label="Pool" sub="Weekly chemistry + summer maintenance + fall winterization" value={hasPool} onToggle={() => setHasPool(!hasPool)} taskCount={lifestyleTaskCount('has_pool')} />
          <ToggleRow label="Solar panels" sub="Annual wash + monthly production check" value={hasSolar} onToggle={() => setHasSolar(!hasSolar)} taskCount={lifestyleTaskCount('has_solar')} />
          <ToggleRow label="Pets at home" sub="Mid-cycle HVAC filter check + register vacuum + yard cleanup" value={hasPets} onToggle={() => setHasPets(!hasPets)} taskCount={lifestyleTaskCount('has_pets')} />
          <ToggleRow label="Kids under 12" sub="Water-temp check, outlet covers, medicine cabinet audit" value={hasKids} onToggle={() => setHasKids(!hasKids)} taskCount={lifestyleTaskCount('has_kids')} />
          <ToggleRow label="HOA" sub="Dues check + annual rules skim" value={hasHoa} onToggle={() => setHasHoa(!hasHoa)} taskCount={lifestyleTaskCount('has_hoa')} />
          <ToggleRow label="Wood stove / fireplace" sub="Annual chimney sweep, firewood, ash safety" value={hasWoodfire} onToggle={() => setHasWoodfire(!hasWoodfire)} taskCount={lifestyleTaskCount('has_woodfire')} />
          <ToggleRow label="Smart-home hub" sub="Battery swap + firmware checks" value={hasSmartHome} onToggle={() => setHasSmartHome(!hasSmartHome)} taskCount={lifestyleTaskCount('has_smart_home')} />
          <ToggleRow label="Short-term rental host" sub="Deep-clean, restock, alarm tests" value={hasStrHost} onToggle={() => setHasStrHost(!hasStrHost)} taskCount={lifestyleTaskCount('has_str_host')} />
          <ToggleRow label="Heavy-snow region" sub="Gutter prep, roof checks, snow blower" value={hasHeavySnow} onToggle={() => setHasHeavySnow(!hasHeavySnow)} taskCount={lifestyleTaskCount('has_heavy_snow')} />
          <ToggleRow label="Garden / vegetable beds" sub="Soil test, bed prep, end-of-season cleanup" value={hasGarden} onToggle={() => setHasGarden(!hasGarden)} taskCount={lifestyleTaskCount('has_garden')} />
          <ToggleRow label="Accessibility / aging-in-place" sub="Grab-bar tests + fall-hazard walk" value={hasAccessibility} onToggle={() => setHasAccessibility(!hasAccessibility)} taskCount={lifestyleTaskCount('has_accessibility')} />
          <ToggleRow label="First-time homeowner" sub="Shutoffs tour + document binder" value={isFirstTime} onToggle={() => setIsFirstTime(!isFirstTime)} taskCount={lifestyleTaskCount('is_first_time')} />
          {/* v35 lifestyles — segments we added in the onboarding rework
              that previously had no Edit Home surface. Mirror the
              onboarding labels exactly so users see the same copy in
              both places. */}
          <ToggleRow label="Travel often (7+ days at a time)" sub="Pre-trip checklist, leak-monitor armed, freeze risk" value={travelsOften} onToggle={() => setTravelsOften(!travelsOften)} taskCount={lifestyleTaskCount('travels_often')} />
          <ToggleRow label="Work from home" sub="2× HVAC duty — mid-cycle filter swap, indoor-air-quality focus" value={worksFromHome} onToggle={() => setWorksFromHome(!worksFromHome)} taskCount={lifestyleTaskCount('works_from_home')} />
          <ToggleRow label="Allergies / asthma" sub="MERV-13 upgrade hint, HEPA filter swap, duct-clean cadence" value={hasAllergies} onToggle={() => setHasAllergies(!hasAllergies)} taskCount={lifestyleTaskCount('has_allergies')} />
          <ToggleRow label="Newborn / under 1" sub="Humidity 40–60%, lead-check, smoke alarm proximity" value={hasNewborn} onToggle={() => setHasNewborn(!hasNewborn)} taskCount={lifestyleTaskCount('has_newborn')} />
          <ToggleRow label="Multi-generational household" sub="In-law unit walk-through, extra water heater + zones" value={multiGenHousehold} onToggle={() => setMultiGenHousehold(!multiGenHousehold)} taskCount={lifestyleTaskCount('multi_gen_household')} />
          <ToggleRow label="Owner over 60" sub="Skip-the-ladder nudges; handrail + nightlight checks" value={over60Owner} onToggle={() => setOver60Owner(!over60Owner)} taskCount={lifestyleTaskCount('over_60_owner')} />
          <ToggleRow label="Livestock or horses" sub="Trough heater, barn vent, fence walk, feed-pest control" value={hasLivestock} onToggle={() => setHasLivestock(!hasLivestock)} taskCount={lifestyleTaskCount('has_livestock')} />
          <ToggleRow label="Frequent contractors (cleaner, lawn, pool)" sub="Access-code rotate, lockbox battery, alarm-code hygiene" value={hasShortTermContractors} onToggle={() => setHasShortTermContractors(!hasShortTermContractors)} taskCount={lifestyleTaskCount('has_short_term_contractors')} />
        </Section>

        <Section title="REGIONAL HAZARDS">
          <Text style={[styles.fieldLabel, { marginBottom: 8 }]}>
            Pick anything your area is prone to. Each unlocks a storm-prep
            bundle — kit refresh, shelter check, defensible space, etc.
          </Text>
          <View style={styles.hazardChipRow}>
            {REGION_HAZARD_ORDER.map((h) => {
              const sel = regionHazards.includes(h);
              return (
                <Pressable
                  key={h}
                  onPress={() =>
                    setRegionHazards((cur) =>
                      cur.includes(h) ? cur.filter((x) => x !== h) : [...cur, h]
                    )
                  }
                  style={({ pressed }) => [
                    styles.hazardChip,
                    sel && styles.hazardChipSel,
                    pressed && { opacity: 0.85 },
                  ]}>
                  <Text style={[styles.hazardChipText, sel && styles.hazardChipTextSel]}>
                    {REGION_HAZARD_LABEL[h]}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </Section>

      </ScrollView>

      {/* Sticky bottom status bar. Auto-save status on the left, manual
          Replan button on the right. Auto-replan was removed in Wave 22
          — instead, planner-affecting changes light up the needs_replan
          flag (shown here as an honey-tinted "Plan needs update" pill)
          and the user kicks the actual regen explicitly. */}
      <StickyBottomBar>
        <View style={styles.statusBlock}>
          <View
            style={[
              styles.savedPill,
              row?.needs_replan === 1 && styles.savedPillPending,
            ]}>
            {savedFlash ? (
              <>
                <Check size={12} color={Palette.cream} strokeWidth={2.6} />
                <Text style={styles.savedPillText}>Saved</Text>
              </>
            ) : row?.needs_replan === 1 ? (
              <Text style={[styles.savedPillText, styles.savedPillTextPending]}>
                Plan needs update — tap Replan now
              </Text>
            ) : (
              <Text style={styles.savedPillText}>Auto-saves as you go</Text>
            )}
          </View>
        </View>
        {/* Explicit Save — the page already auto-saves, but "did it
            take?" anxiety is real. This flushes the pending debounce
            immediately and lights the Saved pill. */}
        <Pressable
          onPress={() => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            void persistNow();
          }}
          disabled={working}
          style={({ pressed }) => [
            styles.saveNowBtn,
            working && { opacity: 0.6 },
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Check size={16} color={Palette.forest} strokeWidth={2.2} />
          <Text style={styles.saveNowText}>Save</Text>
        </Pressable>
        <Pressable
          onPress={save}
          disabled={working}
          style={({ pressed }) => [
            styles.replanBtn,
            working && { opacity: 0.6 },
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Check size={16} color={Palette.cream} strokeWidth={2} />
          <Text style={styles.replanText}>
            {working ? 'Replanning…' : 'Replan now'}
          </Text>
        </Pressable>
      </StickyBottomBar>
      <ReplanSummaryModal
        visible={replanSummary !== null}
        diff={replanSummary}
        loading={replanLoading}
        homeName={row?.name?.trim() || 'this home'}
        onClose={() => {
          setReplanSummary(null);
          setReplanLoading(false);
        }}
        onConfirm={async () => {
          if (!row) return;
          try {
            await regenerateTasksWithDiff(db, row.id);
            bumpDataRevision();
          } finally {
            setReplanSummary(null);
            setReplanLoading(false);
          }
        }}
      />
    </SafeAreaView>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function Pair({ children }: { children: React.ReactNode }) {
  // marginTop matches Field's own marginTop so a Pair following a Field
  // (or a label) gets the same breathing room — keeps the BASICS card
  // from looking like the year/sq-ft fields are jammed against the
  // nickname input above them.
  return (
    <View style={{ flexDirection: 'row', gap: 10, marginTop: Spacing.three }}>
      {children}
    </View>
  );
}

function ToggleRow({
  label,
  sub,
  value,
  onToggle,
  taskCount,
}: {
  label: string;
  sub?: string;
  value: boolean;
  onToggle: () => void;
  /** Number of task rules that key on this lifestyle flag. Rendered
   *  inline as a small "+N tasks" pill so users see the impact of
   *  flipping each toggle — same affordance the onboarding lifestyle
   *  step uses. */
  taskCount?: number;
}) {
  return (
    <Pressable
      onPress={onToggle}
      style={({ pressed }) => [styles.lifestyleRow, pressed && { opacity: 0.85 }]}>
      <View style={{ flex: 1 }}>
        <View style={styles.lifestyleTitleRow}>
          <Text style={styles.lifestyleLabel}>{label}</Text>
          {taskCount != null ? (
            <View style={[styles.lifestyleCountPill, value && styles.lifestyleCountPillSel]}>
              <Text
                style={[
                  styles.lifestyleCountPillText,
                  value && styles.lifestyleCountPillTextSel,
                ]}>
                +{taskCount} task{taskCount === 1 ? '' : 's'}
              </Text>
            </View>
          ) : null}
        </View>
        {sub ? <Text style={styles.lifestyleSub}>{sub}</Text> : null}
      </View>
      <View style={[styles.lifestyleToggle, value && styles.lifestyleToggleOn]}>
        <View style={[styles.lifestyleThumb, value && styles.lifestyleThumbOn]} />
      </View>
    </Pressable>
  );
}

function Field({
  label,
  value,
  onChangeText,
  placeholder,
  inputMode = 'text',
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'numeric' | 'decimal';
}) {
  return (
    <View style={{ flex: 1, marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Palette.inkFaint}
        inputMode={inputMode}
        // Home facts (ZIP, year, sqft…) are never something the browser
        // should autofill from a saved contact card — Chrome's panel was
        // popping over these fields (Wave 47).
        {...webNoAutofill(`edit-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`)}
        style={styles.fieldInput}
      />
    </View>
  );
}

/**
 * Tappable chip row for low-cardinality numeric fields (Stories,
 * Bedrooms, Bathrooms). Reads from / writes to the same string state
 * the typed Field would use, so the auto-save and save() paths don't
 * need to change. The chip whose label matches the current value is
 * highlighted. Tapping the same chip again clears the value back to
 * empty so the user can opt out without typing.
 *
 * Why chips instead of typed input: the user said "less typing, more
 * selecting." For Stories (1-4), Bedrooms (1-6), Bathrooms (1-4 with
 * half steps), the value space is small enough that a chip row is
 * faster, friendlier, and works without a keyboard on mobile.
 */
function ChipNumber({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      {/* Single horizontal-scroll row instead of flex-wrap. Bathrooms
          has 8 options (1/1.5/2/2.5/3/3.5/4+) which broke onto two
          rows on narrow phones — visually messy. Scroll keeps it tidy
          on mobile and the row never wraps. */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={chipNumStyles.scroll}
        contentContainerStyle={chipNumStyles.row}>
        {options.map((opt) => {
          // Match selection on the bare numeric value so trailing-zero
          // forms ("2.0" vs "2") still highlight the right chip.
          const trimmed = value.trim();
          const sel = trimmed === opt || (opt.endsWith('+') && Number(trimmed) >= Number(opt))
            || trimmed === opt.replace(/\+$/, '');
          return (
            <Pressable
              key={opt}
              onPress={() => onChange(sel ? '' : opt.replace(/\+$/, ''))}
              style={({ pressed, hovered }) => [
                chipNumStyles.chip,
                hovered && !sel && chipNumStyles.chipHover,
                sel && chipNumStyles.chipSel,
                pressed && { opacity: 0.85 },
              ]}>
              <Text style={[chipNumStyles.chipText, sel && chipNumStyles.chipTextSel]}>{opt}</Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

const chipNumStyles = StyleSheet.create({
  scroll: {
    marginTop: 8,
    flexGrow: 0,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingRight: 16,
  },
  chip: {
    minWidth: 48,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipHover: {
    borderColor: Palette.forestLight,
    backgroundColor: '#F4F7F2',
  },
  chipSel: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  chipText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
    letterSpacing: 0.2,
  },
  chipTextSel: {
    color: Palette.cream,
  },
});

function PickField<T extends string | null>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | null;
  options: { v: T; label: string }[];
  onChange: (v: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.v === value);
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <Pressable
        onPress={() => setOpen(true)}
        style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }]}>
        <Text style={[styles.pickText, !current && styles.pickPlaceholder]}>
          {current?.label ?? 'Tap to pick'}
        </Text>
      </Pressable>
      <Modal animationType="fade" transparent visible={open} onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setOpen(false)}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{label}</Text>
            <ScrollView style={{ maxHeight: 320 }}>
              {options.map((opt) => {
                const sel = opt.v === value;
                return (
                  <Pressable
                    key={String(opt.v ?? '__null')}
                    onPress={() => {
                      onChange(opt.v);
                      setOpen(false);
                    }}
                    style={({ pressed }) => [
                      styles.modalRow,
                      pressed && { backgroundColor: '#EEF3EE' },
                      sel && { backgroundColor: '#EEF3EE' },
                    ]}>
                    <Text style={styles.modalRowText}>{opt.label}</Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
    paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 24, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, lineHeight: 19, color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  section: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, padding: Spacing.four,
  },
  sectionTitle: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint,
    letterSpacing: 0.6,
  },
  fieldLabel: {
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkSoft,
    letterSpacing: 0.4,
  },
  addressHint: {
    marginTop: 10,
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
    lineHeight: 17,
  },
  // Stories / Bedrooms / Bathrooms share one row on desktop. flexBasis
  // 160 means a narrow phone wraps each onto its own row, but a 560+px
  // column fits all three side-by-side.
  numberRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'flex-start',
  },
  numberCol: {
    flexGrow: 1,
    flexBasis: 160,
    minWidth: 0,
  },
  // BASICS row 1 — Home nickname (wide flex) + Stories (small fixed
  // flex-basis). Wraps to a stack on narrow phones; on desktop the
  // 3-chip stories cluster sits to the right of the nickname input
  // without crowding it.
  nicknameRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'flex-end',
  },
  nicknameField: {
    flexGrow: 1,
    flexBasis: 220,
    minWidth: 0,
  },
  storiesCol: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 180,
    minWidth: 160,
    // Match Field's built-in marginTop so the Stories label aligns
    // with the Home nickname label rather than floating higher.
    marginTop: Spacing.three,
  },
  // Sq Ft slider block — full width on every viewport. Header shows
  // the live numeric value; the slider track sits below; end labels
  // book-end the range.
  sqftBlock: {
    marginTop: Spacing.three,
  },
  sqftHead: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  sqftValue: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.forest,
    letterSpacing: -0.1,
  },
  sqftEnds: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: -4,
    paddingHorizontal: 4,
  },
  sqftEndLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.inkFaint,
    letterSpacing: 0.4,
  },
  // Address (Places) + ZIP share one row on desktop. flex-wrap drops
  // ZIP under address on narrow mobile.
  // Address row — on desktop, Address + ZIP share one row. On mobile,
  // Address wraps to a full-width row and ZIP gets its own COMPACT
  // row below (flexGrow: 0 + small fixed width) so it doesn't blow
  // out into a giant input that takes up half the screen for a
  // 5-digit code.
  addrRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'flex-end',
  },
  addrAddress: {
    flexGrow: 1,
    flexBasis: 280,
    minWidth: 0,
  },
  addrZip: {
    flexGrow: 0,
    flexShrink: 0,
    // Tight 110px width — enough for "12345" plus padding/label, with
    // no extra whitespace tail. On mobile this means ZIP sits left-
    // aligned on its own row, not stretching across the viewport.
    flexBasis: 110,
    width: 110,
    minWidth: 110,
  },
  lifestyleRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    marginTop: Spacing.three,
    padding: 12,
    backgroundColor: Palette.sand,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Palette.line,
  },
  lifestyleTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  lifestyleLabel: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  // "+N tasks" pill next to each lifestyle label. Same shape as the
  // onboarding step's count pill; honey palette so it reads as info
  // not warning. Switches to a saturated honey when the toggle is on,
  // matching the onboarding active state.
  lifestyleCountPill: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: Palette.sand,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  lifestyleCountPillSel: {
    backgroundColor: Palette.honeySoft,
    borderColor: 'rgba(225,163,61,0.4)',
  },
  lifestyleCountPillText: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.inkFaint,
    letterSpacing: 0.3,
  },
  lifestyleCountPillTextSel: {
    color: Palette.honeyDeep,
  },
  lifestyleSub: {
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
    marginTop: 1, lineHeight: 16,
  },
  lifestyleToggle: {
    width: 44, height: 26, borderRadius: 13,
    backgroundColor: Palette.sandDeep, padding: 3, justifyContent: 'center',
  },
  lifestyleToggleOn: { backgroundColor: Palette.forest },
  lifestyleThumb: { width: 20, height: 20, borderRadius: 10, backgroundColor: Palette.cream },
  lifestyleThumbOn: { transform: [{ translateX: 18 }] },
  hazardChipRow: {
    marginTop: 4,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  hazardChip: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
  },
  hazardChipSel: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  hazardChipText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.ink,
  },
  hazardChipTextSel: {
    color: Palette.cream,
  },
  fieldInput: {
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 11,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
  },
  pickBox: {
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 12,
  },
  pickText: { fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink },
  pickPlaceholder: { color: Palette.inkFaint, fontFamily: Fonts.sansMedium },
  backdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand, borderRadius: Radius.xl,
    padding: Spacing.four, width: '100%', maxWidth: 380,
  },
  modalTitle: {
    fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink,
    marginBottom: 10,
  },
  modalRow: { paddingVertical: 12, paddingHorizontal: 12, borderRadius: 10 },
  modalRowText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  saveBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, backgroundColor: Palette.forest,
    paddingVertical: 16, borderRadius: 18,
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
  },
  saveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 15.5 },
  statusBlock: {
    flex: 1,
    minWidth: 0,
    alignItems: 'flex-start',
  },
  savedPill: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 12, paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: '#DCE7E1',
  },
  // Honey-tinted variant for "plan needs update" — same shape as the
  // calm Saved pill but in the warm honey palette so the user reads
  // it as "attention" without it screaming "error" the way clay would.
  savedPillPending: {
    backgroundColor: 'rgba(225,163,61,0.18)',
  },
  savedPillText: {
    fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.forest,
    letterSpacing: 0.3,
  },
  savedPillTextPending: {
    color: Palette.honeyDeep,
  },
  replanBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 7,
    backgroundColor: Palette.forest,
    paddingVertical: 12, paddingHorizontal: 16, borderRadius: 14,
  },
  saveNowBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6,
    backgroundColor: Palette.card,
    borderWidth: 1.5, borderColor: Palette.forest,
    paddingVertical: 12, paddingHorizontal: 14, borderRadius: 14,
    marginRight: 8,
  },
  saveNowText: {
    color: Palette.forest, fontFamily: Fonts.sansBold, fontSize: 13.5,
  },
  replanText: {
    color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 13.5,
  },
  bgPicker: {
    marginTop: 8,
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 16,
    overflow: 'hidden',
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
  },
  bgImage: { width: '100%', height: '100%' },
  bgClear: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'rgba(0,0,0,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bgEmpty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: 16,
  },
  bgEmptyLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.inkSoft,
  },
  bgEmptySub: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkFaint,
    textAlign: 'center',
  },
  bgEmptyOverlay: {
    // Sit on top of the default-image so the "tap to add" copy is
    // readable against the placeholder photo.
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(44,71,56,0.55)',
  },
  bgBusy: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(44,71,56,0.7)',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  bgBusyText: {
    color: Palette.cream,
    fontFamily: Fonts.sansBold,
    fontSize: 13,
  },
});
